import { forwardRef } from "react";
import wekitLogoAsset from "@/assets/wekit-logo.png.asset.json";

export type CardTheme = "apple" | "canva" | "glass" | "dark";

export type CardMatch = {
  /** Career name, verbatim from the sealed payload. */
  title: string;
  /** Formatted salary band, or null when the market layer has no price. */
  salary: string | null;
};

export type CardSignal = {
  /** Career DNA™ signal name, verbatim from the payload. */
  name: string;
  /** 0–100 display value, already normalised by the caller. */
  value: string;
};

export type CareerCardData = {
  /** Primary archetype persona, or null when we have no archetype at all. */
  personaTitle: string | null;
  personaEmoji: string | null;
  /** Primary archetype tagline (canon: ARCHETYPE_TAGLINES). */
  tagline: string | null;
  /** e.g. "with a strong Connector streak" — omitted when there is only one. */
  secondaryLine: string | null;
  /** The personalised headline sentence from the assessment composer. */
  headline: string | null;
  /** Top 3 matches with their salary band. */
  matches: CardMatch[];
  /** Two Career DNA™ signals with values. */
  signals: CardSignal[];
  /** Quiet credibility line: assessed date + short report hash. */
  credibility: string | null;
  handle?: string | null;
};

type ThemeStyles = {
  wrapper: string;
  badge: string;
  title: string;
  subtitle: string;
  sectionLabel: string;
  chip: string;
  careerChip: string;
  footerLabel: string;
  footerValue: string;
  divider: string;
  backdrop?: React.ReactNode;
};

const THEMES: Record<CardTheme, ThemeStyles> = {
  apple: {
    wrapper:
      "bg-[#f5f5f7] text-[#1d1d1f]",
    badge:
      "bg-white border border-[#e5e5e7] text-[#1d1d1f] shadow-sm",
    title: "text-[#1d1d1f]",
    subtitle: "text-[#6e6e73]",
    sectionLabel: "text-[#6e6e73]",
    chip:
      "bg-white border border-[#e5e5e7] text-[#1d1d1f] shadow-sm",
    careerChip:
      "bg-[#1d1d1f] text-white",
    footerLabel: "text-[#6e6e73]",
    footerValue: "text-[#1d1d1f]",
    divider: "border-[#e5e5e7]",
  },
  canva: {
    wrapper:
      "bg-gradient-to-br from-[#FF5F1F] via-[#FF8A4C] to-[#FFD166] text-[#180205]",
    badge:
      "bg-white/95 text-[#180205] border border-white/60",
    title: "text-[#180205]",
    subtitle: "text-[#180205]/80",
    sectionLabel: "text-[#180205]/70",
    chip:
      "bg-white/95 text-[#180205] border border-white/80 shadow-sm",
    careerChip:
      "bg-[#180205] text-white",
    footerLabel: "text-[#180205]/70",
    footerValue: "text-[#180205]",
    divider: "border-[#180205]/15",
  },
  glass: {
    wrapper:
      "bg-gradient-to-br from-[#0818A8] via-[#0205F4] to-[#00665B] text-white",
    badge:
      "bg-white/15 backdrop-blur-md border border-white/30 text-white",
    title: "text-white",
    subtitle: "text-white/80",
    sectionLabel: "text-white/70",
    chip:
      "bg-white/15 backdrop-blur-md border border-white/30 text-white",
    careerChip:
      "bg-white text-[#0818A8]",
    footerLabel: "text-white/70",
    footerValue: "text-white",
    divider: "border-white/20",
    backdrop: (
      <>
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#FF5F1F]/30 blur-3xl" />
      </>
    ),
  },
  dark: {
    wrapper:
      "bg-[#0a0a0a] text-white",
    badge:
      "bg-white/5 border border-white/10 text-white",
    title: "text-white",
    subtitle: "text-white/60",
    sectionLabel: "text-white/50",
    chip:
      "bg-white/5 border border-white/10 text-white",
    careerChip:
      "bg-[#00665B] text-white",
    footerLabel: "text-white/50",
    footerValue: "text-white",
    divider: "border-white/10",
    backdrop: (
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-[#00665B] blur-[140px]" />
      </div>
    ),
  },
};

interface Props {
  data: CareerCardData;
  theme: CardTheme;
}

/** Title size steps down so a long persona never wraps past two lines. */
function personaSize(title: string): number {
  if (title.length > 34) return 72;
  if (title.length > 26) return 86;
  return 104;
}

/** The headline is one sentence, but its length varies a lot. */
function headlineSize(text: string): number {
  if (text.length > 190) return 26;
  if (text.length > 140) return 30;
  return 34;
}

const CareerCard = forwardRef<HTMLDivElement, Props>(({ data, theme }, ref) => {
  const t = THEMES[theme];
  const hasPersona = Boolean(data.personaTitle);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${t.wrapper}`}
      style={{
        width: 1080,
        height: 1350,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {t.backdrop}

      <div className="relative h-full w-full flex flex-col p-16">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className={`inline-flex items-center gap-3 rounded-full px-5 py-2.5 text-base font-medium ${t.badge}`}>
            {data.personaEmoji ? (
              <span className="text-xl leading-none">{data.personaEmoji}</span>
            ) : null}
            Career Identity
          </div>
          <img
            src={wekitLogoAsset.url}
            alt="WeKIT™"
            crossOrigin="anonymous"
            className="h-14 w-14 rounded-md object-contain"
          />
        </div>

        {/* Persona — omitted entirely when we have no archetype. */}
        {hasPersona ? (
          <div className="mt-10">
            <p className={`text-lg uppercase tracking-[0.2em] font-semibold ${t.sectionLabel}`}>
              I am a
            </p>
            <h1
              className={`mt-3 font-bold leading-[1.05] tracking-tight ${t.title}`}
              style={{
                fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif",
                fontSize: personaSize(data.personaTitle!),
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {data.personaTitle}
            </h1>
            {data.secondaryLine ? (
              <p className={`mt-3 text-2xl font-medium ${t.subtitle}`}>
                {data.secondaryLine}
              </p>
            ) : null}
            {data.tagline ? (
              <p className={`mt-4 text-2xl leading-snug ${t.subtitle}`}>
                {data.tagline}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="mt-10">
            <h1
              className={`font-bold leading-[1.05] tracking-tight ${t.title}`}
              style={{
                fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif",
                fontSize: 86,
              }}
            >
              Career Identity
            </h1>
          </div>
        )}

        {/* Personalised headline */}
        {data.headline ? (
          <p
            className={`mt-8 leading-snug ${t.title}`}
            style={{
              fontSize: headlineSize(data.headline),
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {data.headline}
          </p>
        ) : null}

        {/* Career DNA signals */}
        {data.signals.length ? (
          <div className="mt-10">
            <p className={`text-sm uppercase tracking-[0.2em] font-semibold ${t.sectionLabel}`}>
              Career DNA™
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {data.signals.slice(0, 2).map((s) => (
                <span
                  key={s.name}
                  className={`rounded-full px-5 py-2.5 text-xl font-semibold ${t.chip}`}
                  style={{ maxWidth: 460, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {s.name} · {s.value}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* Matches with salary bands */}
        {data.matches.length ? (
          <div className="mt-9">
            <p className={`text-sm uppercase tracking-[0.2em] font-semibold ${t.sectionLabel}`}>
              Top career matches
            </p>
            <div className="mt-4 flex flex-col gap-3">
              {data.matches.slice(0, 3).map((m) => (
                <span
                  key={m.title}
                  className={`rounded-2xl px-5 py-3 text-xl font-semibold self-start ${t.careerChip}`}
                  style={{ maxWidth: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {m.title}
                  {m.salary ? ` · ${m.salary}` : ""}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex-1" />

        {/* Footer */}
        <div className={`mt-8 pt-7 border-t ${t.divider}`}>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className={`text-base ${t.footerLabel}`}>Powered by</p>
              <p
                className={`text-3xl font-bold leading-tight ${t.footerValue}`}
                style={{ fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif" }}
              >
                WeKIT™ Career Clarity 360
              </p>
              <p className={`mt-2 text-base italic ${t.footerLabel}`}>
                Rooting You In Your Purpose
              </p>
            </div>
            {data.handle && (
              <div className="text-right shrink-0">
                <p className={`text-base ${t.footerLabel}`}>Take yours</p>
                <p
                  className={`text-2xl font-semibold ${t.footerValue}`}
                  style={{ fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif" }}
                >
                  wekit.ai/ref/{data.handle}
                </p>
              </div>
            )}
          </div>
          {data.credibility ? (
            <p className={`mt-4 text-sm font-mono ${t.footerLabel}`}>
              {data.credibility}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
});
CareerCard.displayName = "CareerCard";

export default CareerCard;
